import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Wrench, Plus, CheckCircle2 } from 'lucide-react';
import { api, ApiError } from '@/lib/api';
import { useApi } from '@/lib/useApi';
import { useAuth } from '@/lib/auth';
import { money, humanise, relative, date } from '@/lib/format';
import {
  Badge, Button, Card, EmptyState, Field, Input, LoadingBlock, Modal, MoneyInput,
  PageHeader, Select, Stat, Tabs, Textarea, useToast, statusTone,
} from '@/components/ui';
import type {
  MaintenanceRequest, Property, Room, Tenant, MaintenanceStatus, MaintenancePriority,
} from '@shared/types';

const STATUSES: MaintenanceStatus[] = ['open', 'acknowledged', 'in_progress', 'resolved', 'closed', 'rejected'];
const PRIORITIES: MaintenancePriority[] = ['low', 'normal', 'high', 'urgent'];
const CATEGORIES = ['general', 'plumbing', 'electrical', 'appliance', 'structural', 'pest', 'security', 'cleaning'];

export default function Maintenance() {
  const { currency, can } = useAuth();

  const [tab, setTab] = useState('open');
  const [propertyId, setPropertyId] = useState('');
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState<MaintenanceRequest | null>(null);

  const { data: properties } = useApi<Property[]>('/properties');
  const { data, loading, reload } = useApi<MaintenanceRequest[]>('/maintenance', {
    property_id: propertyId || undefined,
  });

  const all = data ?? [];
  const openItems = all.filter((m) => ['open', 'acknowledged', 'in_progress'].includes(m.status));
  const done = all.filter((m) => ['resolved', 'closed', 'rejected'].includes(m.status));
  const shown = tab === 'open' ? openItems : tab === 'done' ? done : all;
  const totalCost = all.reduce((s, m) => s + m.cost_cents, 0);

  return (
    <div className="space-y-6">
      <PageHeader
        title="Maintenance"
        subtitle="Requests raised by tenants and jobs logged by staff"
        action={can('staff') && (
          <Button variant="primary" icon={<Plus className="h-4 w-4" />} onClick={() => setCreating(true)}>
            Log a job
          </Button>
        )}
      />

      <div className="grid gap-4 sm:grid-cols-3">
        <Stat
          label="Open"
          value={String(openItems.length)}
          tone={openItems.some((m) => m.priority === 'urgent') ? 'danger' : undefined}
          hint={`${openItems.filter((m) => m.priority === 'urgent').length} urgent`}
        />
        <Stat label="Resolved" value={String(done.length)} tone="ok" />
        <Stat label="Total spent" value={money(totalCost, currency)} hint="Across all jobs" />
      </div>

      <div className="flex flex-wrap gap-3">
        <Select value={propertyId} onChange={(e) => setPropertyId(e.target.value)} className="w-auto min-w-[180px]">
          <option value="">All properties</option>
          {(properties ?? []).map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
        </Select>
      </div>

      <Tabs
        active={tab}
        onChange={setTab}
        tabs={[
          { id: 'open', label: 'Open', count: openItems.length },
          { id: 'done', label: 'Closed', count: done.length },
          { id: 'all', label: 'All', count: all.length },
        ]}
      />

      {loading ? (
        <LoadingBlock rows={5} />
      ) : shown.length === 0 ? (
        <Card>
          <EmptyState
            icon={tab === 'open' ? <CheckCircle2 className="h-6 w-6" /> : <Wrench className="h-6 w-6" />}
            title={tab === 'open' ? 'Nothing outstanding' : 'No requests'}
            message={tab === 'open' ? 'Every reported problem has been dealt with.' : undefined}
          />
        </Card>
      ) : (
        <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
          {shown.map((m) => (
            <button
              key={m.id}
              onClick={() => can('staff') && setEditing(m)}
              className="text-left panel hover:border-faint transition-all"
            >
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <p className="font-semibold truncate">{m.title}</p>
                  <p className="text-xs text-faint mt-0.5 truncate">
                    {m.room_name ? `${m.room_name} · ` : ''}{m.property_name}
                  </p>
                </div>
                <Badge tone={statusTone(m.priority)}>{humanise(m.priority)}</Badge>
              </div>

              {m.description && (
                <p className="text-sm text-muted mt-3 line-clamp-2 leading-relaxed">{m.description}</p>
              )}

              <div className="flex items-center justify-between gap-2 mt-4 pt-3 border-t border-line">
                <Badge tone={statusTone(m.status)} dot>{humanise(m.status)}</Badge>
                <span className="text-xs text-faint">{relative(m.created_at)}</span>
              </div>

              {(m.tenant_name || m.cost_cents > 0) && (
                <div className="flex items-center justify-between gap-2 mt-2 text-xs">
                  {m.tenant_name ? (
                    <Link
                      to={`/app/tenants/${m.tenant_id}`}
                      className="link truncate"
                      onClick={(e) => e.stopPropagation()}
                    >
                      {m.tenant_name}
                    </Link>
                  ) : <span className="text-faint">Staff logged</span>}
                  {m.cost_cents > 0 && (
                    <span className="tnum font-medium">{money(m.cost_cents, currency)}</span>
                  )}
                </div>
              )}
            </button>
          ))}
        </div>
      )}

      {(creating || editing) && (
        <MaintenanceModal
          request={editing ?? undefined}
          onClose={() => { setCreating(false); setEditing(null); }}
          onDone={() => { setCreating(false); setEditing(null); reload(); }}
        />
      )}
    </div>
  );
}

function MaintenanceModal({ request, onClose, onDone }: {
  request?: MaintenanceRequest; onClose: () => void; onDone: () => void;
}) {
  const { currency } = useAuth();
  const toast = useToast();
  const [busy, setBusy] = useState(false);

  const [propertyId, setPropertyId] = useState(request?.property_id ?? '');
  const [roomId, setRoomId] = useState(request?.room_id ?? '');
  const [tenantId, setTenantId] = useState(request?.tenant_id ?? '');
  const [title, setTitle] = useState(request?.title ?? '');
  const [description, setDescription] = useState(request?.description ?? '');
  const [category, setCategory] = useState(request?.category ?? 'general');
  const [priority, setPriority] = useState<MaintenancePriority>(request?.priority ?? 'normal');
  const [status, setStatus] = useState<MaintenanceStatus>(request?.status ?? 'open');
  const [assignedTo, setAssignedTo] = useState(request?.assigned_to ?? '');
  const [costCents, setCostCents] = useState(request?.cost_cents ?? 0);
  const [resolution, setResolution] = useState(request?.resolution ?? '');

  const { data: properties } = useApi<Property[]>('/properties');
  const { data: rooms } = useApi<Room[]>(propertyId ? '/rooms' : null, { property_id: propertyId });
  const { data: tenants } = useApi<Tenant[]>('/tenants', { property_id: propertyId || undefined });

  async function submit() {
    if (!propertyId) { toast.error('Choose a property'); return; }
    if (!title.trim()) { toast.error('Describe the problem in a few words'); return; }

    setBusy(true);
    try {
      const payload = {
        property_id: propertyId,
        room_id: roomId || null,
        tenant_id: tenantId || null,
        title: title.trim(),
        description: description || null,
        category,
        priority,
        status,
        assigned_to: assignedTo || null,
        cost_cents: costCents,
        resolution: resolution || null,
      };
      if (request) await api.patch(`/maintenance/${request.id}`, payload);
      else await api.post('/maintenance', payload);
      toast.success(request ? 'Request updated' : 'Job logged');
      onDone();
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Could not save it');
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal
      open
      onClose={onClose}
      title={request ? 'Update request' : 'Log a maintenance job'}
      subtitle={request?.tenant_name ? `Reported by ${request.tenant_name} · ${date(request.created_at)}` : undefined}
      footer={
        <>
          <Button onClick={onClose} disabled={busy}>Cancel</Button>
          <Button variant="primary" onClick={() => void submit()} loading={busy}>Save</Button>
        </>
      }
    >
      <div className="space-y-4">
        <div className="grid sm:grid-cols-2 gap-4">
          <Field label="Property" required>
            <Select value={propertyId} onChange={(e) => { setPropertyId(e.target.value); setRoomId(''); }}>
              <option value="">Choose a property…</option>
              {(properties ?? []).map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
            </Select>
          </Field>
          <Field label="Room">
            <Select value={roomId} onChange={(e) => setRoomId(e.target.value)} disabled={!propertyId}>
              <option value="">Whole property</option>
              {(rooms ?? []).map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}
            </Select>
          </Field>
        </div>

        <Field label="Reported by">
          <Select value={tenantId} onChange={(e) => setTenantId(e.target.value)}>
            <option value="">Staff / not tenant-reported</option>
            {(tenants ?? []).map((t) => <option key={t.id} value={t.id}>{t.full_name}</option>)}
          </Select>
        </Field>

        <Field label="Title" required>
          <Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Kitchen tap dripping" />
        </Field>

        <Field label="Description">
          <Textarea value={description} onChange={(e) => setDescription(e.target.value)} rows={3} />
        </Field>

        <div className="grid sm:grid-cols-3 gap-4">
          <Field label="Category">
            <Select value={category} onChange={(e) => setCategory(e.target.value)}>
              {CATEGORIES.map((c) => <option key={c} value={c}>{humanise(c)}</option>)}
            </Select>
          </Field>
          <Field label="Priority">
            <Select value={priority} onChange={(e) => setPriority(e.target.value as MaintenancePriority)}>
              {PRIORITIES.map((p) => <option key={p} value={p}>{humanise(p)}</option>)}
            </Select>
          </Field>
          <Field label="Status">
            <Select value={status} onChange={(e) => setStatus(e.target.value as MaintenanceStatus)}>
              {STATUSES.map((s) => <option key={s} value={s}>{humanise(s)}</option>)}
            </Select>
          </Field>
        </div>

        <div className="grid sm:grid-cols-2 gap-4">
          <Field label="Assigned to" hint="Contractor or staff member">
            <Input value={assignedTo} onChange={(e) => setAssignedTo(e.target.value)} placeholder="Otieno Contractors" />
          </Field>
          <Field label="Cost">
            <MoneyInput valueCents={costCents} onChangeCents={setCostCents} currency={currency} />
          </Field>
        </div>

        {['resolved', 'closed', 'rejected'].includes(status) && (
          <Field label="Resolution" hint="Visible to the tenant who reported it">
            <Textarea
              value={resolution}
              onChange={(e) => setResolution(e.target.value)}
              rows={2}
              placeholder="Washer replaced, tap tested and no longer dripping."
            />
          </Field>
        )}
      </div>
    </Modal>
  );
}
